import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { isoToDDMMYYYY as formatDate } from '../utils/dateFormat.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

registerAllModules()

const REALTIME_DEBOUNCE = 1500

const SCHEDULE_LABELS = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ENGLISH_QUARTER_DAYS: 'English Quarter Days',
  SCOTTISH_QUARTER_DAYS: 'Scottish Quarter Days',
  ANNUALLY: 'Annually'
}

const REVIEW_TYPE_LABELS = {
  PCT_OF_MARKET_RENT: '% of Market Rent',
  FIXED_PCT_CHANGE: 'Fixed % Change',
  FIXED_AMOUNT_CHANGE: 'Fixed Amount Change',
  SET_RENT_AMOUNT: 'Set Rent Amount'
}

const MOVEMENT_LABELS = {
  UPWARD_ONLY: 'Upward Only',
  UP_OR_DOWN: 'Up or Down',
  'N/A': 'N/A'
}

const SKIP_REASON_LABELS = {
  outside_lease: 'Outside lease',
  missing_contracted_rent: 'No contracted rent',
  missing_market_rent: 'No market rent',
  invalid_input: 'Invalid input'
}

const REVIEW_TYPES = Object.keys(REVIEW_TYPE_LABELS)
const PAYMENT_FREQUENCIES = Object.keys(SCHEDULE_LABELS)

// Tenant gets its own group cell so the freeze-boundary merge bug
// (Handsontable splits cross-boundary merges) doesn't corrupt headers.
const COLUMNS = [
  { key: 'tenant_name',              group: 'tenant',             label: 'Tenant',                 type: 'text' },
  { key: 'review_no',                group: 'general',            label: 'Review No.',             type: 'number' },
  { key: 'review_date',              group: 'general',            label: 'Review Date',            type: 'date' },
  { key: 'review_type',              group: 'general',            label: 'Review Type',            type: 'review_type' },
  { key: 'payment_frequency',        group: 'general',            label: 'Payment Frequency',      type: 'payment_frequency' },
  { key: 'input_value',              group: 'input_terms',        label: 'Input Value',            type: 'money' },
  { key: 'movement_allowed',         group: 'input_terms',        label: 'Movement Allowed',       type: 'movement' },
  { key: 'original_rent',            group: 'review_calculation', label: 'Original Rent',          type: 'money' },
  { key: 'market_rent_at_review',    group: 'review_calculation', label: 'Market Rent at Review',  type: 'money' },
  { key: 'calculated_review_rent',   group: 'review_calculation', label: 'Calculated Review Rent', type: 'money' },
  { key: 'reviewed_rent',            group: 'review_calculation', label: 'Reviewed Rent',          type: 'money' },
  { key: 'uplift',                   group: 'review_calculation', label: 'Uplift',                 type: 'money' },
  { key: 'cumulative_uplift',        group: 'review_calculation', label: 'Cumulative Uplift',      type: 'money' },
  { key: 'periodic_uplift',          group: 'review_calculation', label: 'Periodic Uplift',        type: 'money' },
  { key: 'cumulative_period_uplift', group: 'review_calculation', label: 'Cumulative Period Uplift', type: 'money' },
  { key: 'effective_from',           group: 'effective_period',   label: 'Effective From',         type: 'date' },
  { key: 'effective_to',             group: 'effective_period',   label: 'Effective To',           type: 'date' },
  { key: 'skip_reason',              group: 'effective_period',   label: 'Skip Reason',            type: 'skip_reason' }
]

const GROUP_LABELS = {
  tenant: '',
  general: 'General',
  input_terms: 'Input Terms',
  review_calculation: 'Review Calculation',
  effective_period: 'Effective Period'
}

const RIGHT_ALIGNED_TYPES = new Set([ 'money', 'number' ])

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatCell(column, row) {
  const raw = row[column.key]
  if (raw === null || raw === undefined) return ''
  switch (column.type) {
    case 'date':              return formatDate(raw)
    case 'money':             return formatMoney(raw)
    case 'review_type':       return REVIEW_TYPE_LABELS[raw] || raw
    case 'payment_frequency': return SCHEDULE_LABELS[raw] || raw
    case 'movement':          return MOVEMENT_LABELS[raw] || raw
    case 'skip_reason':       return SKIP_REASON_LABELS[raw] || raw
    default:                  return String(raw)
  }
}

function buildGroupHeaderRow(columns) {
  const cells = []
  const mergeRanges = []
  let i = 0
  while (i < columns.length) {
    let j = i
    while (j < columns.length && columns[j].group === columns[i].group) j += 1
    cells.push(GROUP_LABELS[columns[i].group])
    for (let k = i + 1; k < j; k += 1) cells.push('')
    if (j - i > 1) mergeRanges.push({ row: 0, col: i, rowspan: 1, colspan: j - i })
    i = j
  }
  return { row: cells, merges: mergeRanges }
}

// Schedule debug table columns (Doc 14 §33). Plain flat columns — no nested
// header groups — this is an audit/debug panel, not a presentation surface.
const SCHEDULE_COLUMNS = [
  { key: 'tenant_name',          label: 'Tenant',               type: 'text' },
  { key: 'review_no',            label: 'Review No.',           type: 'number' },
  { key: 'review_date',          label: 'Review Date',          type: 'date' },
  { key: 'review_type',          label: 'Review Type',          type: 'review_type' },
  { key: 'payment_frequency',    label: 'Payment Frequency',    type: 'payment_frequency' },
  { key: 'payment_period_start', label: 'Payment Period Start', type: 'date' },
  { key: 'payment_period_end',   label: 'Payment Period End',   type: 'date' },
  { key: 'effective_from',       label: 'Effective From',       type: 'date' },
  { key: 'effective_to',         label: 'Effective To',         type: 'date' },
  { key: 'period_days',          label: 'Period Days',          type: 'number' },
  { key: 'segment_count',        label: 'Segments',             type: 'number' },
  { key: 'cash_flow_amount',     label: 'Cash Flow Amount',     type: 'money' }
]

export default function RentReviewEventsTable({ valuationId, dataUrl }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [rows, setRows] = useState([])
  const [scheduleRows, setScheduleRows] = useState([])
  const [allocationSegments, setAllocationSegments] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [tenantNameFilter, setTenantNameFilter] = useState('')
  const [reviewTypeFilter, setReviewTypeFilter] = useState('all')
  const [paymentFrequencyFilter, setPaymentFrequencyFilter] = useState('all')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const fetchRef = useRef(null)

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const res = await fetch(dataUrl, { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) {
        const code = json.error || `Request failed (${res.status})`
        throw new Error(code === 'access_denied' ? "You don't have access to this valuation." : code)
      }
      const data = json.data || {}
      setRows(data.rows || [])
      const schedule = data.payment_schedule || {}
      setScheduleRows(schedule.schedule_rows || [])
      setAllocationSegments(schedule.allocation_segments || [])
    } catch (err) {
      setError(err.message || 'Failed to load')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [dataUrl])

  useEffect(() => { fetchRef.current = fetchData }, [fetchData])
  useEffect(() => { fetchData() }, [fetchData])

  // Live-recalc on Tenant / RentReview saves via the shared ValuationChannel.
  useEffect(() => {
    if (!valuationId) return undefined
    let debounceTimer = null

    const subscription = cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: valuationId },
      {
        received() {
          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => { fetchRef.current?.(true) }, REALTIME_DEBOUNCE)
        }
      }
    )

    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [valuationId])

  const filteredRows = useMemo(() => rows.filter(r => {
    if (tenantNameFilter && !String(r.tenant_name || '').toLowerCase().includes(tenantNameFilter.toLowerCase())) return false
    if (reviewTypeFilter !== 'all' && r.review_type !== reviewTypeFilter) return false
    if (paymentFrequencyFilter !== 'all' && r.payment_frequency !== paymentFrequencyFilter) return false
    if (dateFromFilter && r.review_date && r.review_date < dateFromFilter) return false
    if (dateToFilter && r.review_date && r.review_date > dateToFilter) return false
    return true
  }), [rows, tenantNameFilter, reviewTypeFilter, paymentFrequencyFilter, dateFromFilter, dateToFilter])

  const groupHeader = useMemo(() => buildGroupHeaderRow(COLUMNS), [])
  const columnHeaderRow = useMemo(() => COLUMNS.map(c => c.label), [])

  const tableData = useMemo(() => {
    const dataRows = filteredRows.map(row => COLUMNS.map(col => formatCell(col, row)))
    return [ groupHeader.row, columnHeaderRow, ...dataRows ]
  }, [filteredRows, groupHeader, columnHeaderRow])

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    )
  }

  if (error) return <div className="alert alert-danger m-3">{error}</div>

  return (
    <div className="rent-review-events-container">
      <div className="d-flex flex-wrap gap-2 px-3 py-2 align-items-center">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setShowSchedule(!showSchedule)}
        >
          {showSchedule ? 'Hide Payment Schedule' : 'Show Payment Schedule'}
          {` (${scheduleRows.length} rows, ${allocationSegments.length} segments)`}
        </button>

        {showFilters && (
          <>
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 small text-muted">Tenant:</label>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ width: 180 }}
                value={tenantNameFilter}
                onChange={e => setTenantNameFilter(e.target.value)}
              />
            </div>
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 small text-muted">Type:</label>
              <select
                className="form-select form-select-sm"
                style={{ width: 200 }}
                value={reviewTypeFilter}
                onChange={e => setReviewTypeFilter(e.target.value)}
              >
                <option value="all">All</option>
                {REVIEW_TYPES.map(v => (
                  <option key={v} value={v}>{REVIEW_TYPE_LABELS[v]}</option>
                ))}
              </select>
            </div>
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 small text-muted">Frequency:</label>
              <select
                className="form-select form-select-sm"
                style={{ width: 180 }}
                value={paymentFrequencyFilter}
                onChange={e => setPaymentFrequencyFilter(e.target.value)}
              >
                <option value="all">All</option>
                {PAYMENT_FREQUENCIES.map(v => (
                  <option key={v} value={v}>{SCHEDULE_LABELS[v]}</option>
                ))}
              </select>
            </div>
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 small text-muted">From:</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={dateFromFilter}
                onChange={e => setDateFromFilter(e.target.value)}
              />
            </div>
            <div className="d-flex align-items-center gap-1">
              <label className="form-label mb-0 small text-muted">To:</label>
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                value={dateToFilter}
                onChange={e => setDateToFilter(e.target.value)}
              />
            </div>
          </>
        )}

        <span className="ms-auto text-muted small">
          {filteredRows.length} of {rows.length} events
        </span>
      </div>

      {filteredRows.length === 0 ? (
        <div className="text-muted text-center py-5">
          {rows.length === 0
            ? "No rent reviews entered yet. Open a tenant's Rent Reviews modal in the Rent Roll to add reviews."
            : 'No rows match the current filters.'}
        </div>
      ) : (
        <div className="cash-flow-table-viewport">
          <HotTable
            data={tableData}
            rowHeaders={false}
            colHeaders={false}
            fixedColumnsStart={1}
            fixedRowsTop={2}
            themeName="ht-theme-horizon"
            licenseKey={HOT_LICENSE_KEY}
            sanitizer={sanitizeHandsontableHtml}
            stretchH="none"
            width="100%"
            height="auto"
            readOnly={true}
            manualColumnResize={true}
            manualRowResize={false}
            mergeCells={groupHeader.merges}
            cells={(row, col) => {
              if (row === 0) return { readOnly: true, className: 'htCenter htMiddle leasing-events-group-header' }
              if (row === 1) return { readOnly: true, className: 'htCenter htMiddle leasing-events-col-header' }
              const column = COLUMNS[col]
              const rightAligned = RIGHT_ALIGNED_TYPES.has(column?.type)
              return { readOnly: true, className: rightAligned ? 'htRight' : 'htLeft' }
            }}
          />
        </div>
      )}

      {showSchedule && (
        <PaymentScheduleDebug
          scheduleRows={scheduleRows}
          allocationSegments={allocationSegments}
        />
      )}
    </div>
  )
}

function PaymentScheduleDebug({ scheduleRows, allocationSegments }) {
  const headerRow = useMemo(() => SCHEDULE_COLUMNS.map(c => c.label), [])
  const data = useMemo(() => {
    const rows = scheduleRows.map(row => SCHEDULE_COLUMNS.map(col => formatCell(col, row)))
    return [ headerRow, ...rows ]
  }, [scheduleRows, headerRow])

  if (scheduleRows.length === 0) {
    return (
      <div className="text-muted small text-center py-4 border-top">
        No payment schedule rows. Reviews must have computed Cumulative Period Uplift to be allocated.
      </div>
    )
  }

  return (
    <div className="border-top pt-3 mt-3">
      <div className="px-3 mb-2 small text-muted">
        <strong>Payment Schedule</strong> · {scheduleRows.length} rows · {allocationSegments.length} segments
      </div>
      <div className="cash-flow-table-viewport">
        <HotTable
          data={data}
          rowHeaders={false}
          colHeaders={false}
          fixedRowsTop={1}
          themeName="ht-theme-horizon"
          licenseKey={HOT_LICENSE_KEY}
          sanitizer={sanitizeHandsontableHtml}
          stretchH="none"
          width="100%"
          height="auto"
          readOnly={true}
          manualColumnResize={true}
          manualRowResize={false}
          cells={(row, col) => {
            if (row === 0) return { readOnly: true, className: 'htCenter htMiddle leasing-events-col-header' }
            const column = SCHEDULE_COLUMNS[col]
            const rightAligned = RIGHT_ALIGNED_TYPES.has(column?.type)
            return { readOnly: true, className: rightAligned ? 'htRight' : 'htLeft' }
          }}
        />
      </div>
    </div>
  )
}
